import { useState, useContext, ChangeEvent, useEffect } from 'react';
import {
  FormsEnum,
  HiProConsent,
  ConsentsEnum,
  InstallmentsEnum,
} from 'app/shared/enums';

import {
  HiProConsentGroup,
  HiProConsentWithTextDto,
} from 'app/shared/interfaces/HiProApplicationResponse.interface';
import { MubiInstallmentsContext } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments.context';
import { ApplicationDataFormProps } from './ApplicationDataForm.interfaces';
import { useForm } from 'app/shared/hooks/useForm';

const consentGroupTitle = [
  {
    text: HiProConsent.GroupConsent1,
    mandatory: true,
    code: ConsentsEnum.Group0,
  },
  {
    text: HiProConsent.GroupConsent2,
    mandatory: true,
    code: ConsentsEnum.Group1,
  },
  {
    text: HiProConsent.GroupConsent3,
    mandatory: false,
    code: ConsentsEnum.Group2,
  },
];

export const hideMarketingConsentCode = ConsentsEnum.ACCEPTMARKETINGCONTACT;

const configConsent = new Map<ConsentsEnum, [string[], string]>([
  [
    ConsentsEnum.Group0,
    [
      [
        ConsentsEnum.DPDLOWERTHANTHRESHOLD,
        ConsentsEnum.ECONOMICBUREAUSACCEPTED,
        ConsentsEnum.ERIFCHECKACCEPTED,
        ConsentsEnum.BIKCHECKACCEPTED,
      ],
      ConsentsEnum.HiProFirstGroup,
    ],
  ],
  [
    ConsentsEnum.Group1,
    [
      [
        ConsentsEnum.CrifAddressDataSharingConsent,
        ConsentsEnum.CrifDataDownloadConsent,
        ConsentsEnum.CrifDataSharingConsent,
      ],
      ConsentsEnum.HiProSecondGroup,
    ],
  ],
  [
    ConsentsEnum.Group2,
    [
      [ConsentsEnum.ACCEPTMARKETINGAUTOMATEDCONTACT],
      ConsentsEnum.HiProThirdGroup,
    ],
  ],
]);

const formatBikConsent = (text: string) => {
  const formatText = text
    .replace(
      `  1.${String.fromCharCode(160)}${String.fromCharCode(
        160
      )}${String.fromCharCode(160)}`,
      '<br></br>1.'
    )
    .replace(
      `  2.${String.fromCharCode(160)}${String.fromCharCode(
        160
      )}${String.fromCharCode(160)}`,
      '<br></br>2.'
    );

  return formatText;
};

export function useConsentHiPro({ change }: ApplicationDataFormProps) {
  const { hiProConsents, mubiInstallmentsDataLayerEvent } = useContext(
    MubiInstallmentsContext
  );

  const [groupedConsents, setGroupedConsents] = useState<
    HiProConsentGroup[] | undefined
  >(undefined);
  const { formValues } = useForm(FormsEnum.MubiInstallmentsDataForm);

  const [
    allRequiredConsentsSelected,
    setAllRequiredConsentsSelected,
  ] = useState(false);

  const emitEvent = (eventName: string) => {
    mubiInstallmentsDataLayerEvent(
      eventName,
      InstallmentsEnum.EventStepNamePersonalData
    );
  };

  useEffect(() => {
    const formatConsent = hiProConsents?.map((consent) => {
      if (consent.code === ConsentsEnum.BIKCHECKACCEPTED) {
        return {
          ...consent,
          text: formatBikConsent(consent.text),
        };
      }

      return consent;
    });

    const filteredConsent = consentGroupTitle.map(
      ({ text, mandatory, code }) => {
        const groupConsents = formatConsent?.filter((consent) => {
          const groupConsents = configConsent.get(code);
          if (!groupConsents) {
            return false;
          }
          return groupConsents[0].includes(consent.code);
        });

        return {
          text,
          mandatory,
          groupConsents,
          code,
        };
      }
    );
    setGroupedConsents(filteredConsent);
  }, [hiProConsents]);

  const mainConsent = hiProConsents?.find(
    (consent) => consent.code === ConsentsEnum.WONGATERMSAGREED
  );

  const handleTickRequiredConsentsChange = (
    e: ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLLabelElement>,
    checked: boolean
  ) => {
    (hiProConsents as Array<HiProConsentWithTextDto>).forEach((consent) => {
      if (consent.mandatory) {
        change(consent.code, checked);
      } else {
        change(consent.code, false);
      }
    });
    change(ConsentsEnum.Group0, checked);
    change(ConsentsEnum.Group1, checked);
    change(ConsentsEnum.Group2, false);
    setAllRequiredConsentsSelected(checked);

    if (checked) {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventNameAllConsents,
        InstallmentsEnum.EventStepNamePersonalData
      );
    }
  };

  const handleChangeGroupConsents = (
    value: ChangeEvent<HTMLInputElement>,
    code: ConsentsEnum
  ) => {
    const groupConsents = configConsent.get(code);

    if (!groupConsents) {
      return;
    }

    (hiProConsents as Array<HiProConsentWithTextDto>).forEach((consent) => {
      if (groupConsents[0].includes(consent.code)) {
        change(consent.code, value);
      }
    });

    if (value) {
      emitEvent(groupConsents[1]);
    }
  };

  const handleMainConsentChange = (value: ChangeEvent<HTMLInputElement>) => {
    if (value) {
      emitEvent(InstallmentsEnum.EventNameWongaTerms);
    }
  };

  const handleUnCheckedGroup = (
    value: ChangeEvent<HTMLInputElement>,
    codeGroup: ConsentsEnum,
    codeConsent: string
  ) => {
    if (!value) {
      change(codeGroup, value);
    } else {
      const groupConsents = configConsent.get(codeGroup);

      if (!groupConsents) {
        return;
      }

      const isAllGroupConsentsChecked = groupConsents[0]
        .filter((consent) => consent !== codeConsent)
        .every((consent) => {
          return formValues[consent];
        });

      change(codeGroup, isAllGroupConsentsChecked);
    }
  };

  return {
    groupedConsents,
    mainConsent,
    handleTickRequiredConsentsChange,
    allRequiredConsentsSelected,
    handleChangeGroupConsents,
    handleUnCheckedGroup,
    handleMainConsentChange,
  };
}
